import { TmBffHttpClient } from '@treasury/core/http/tm-bff-http-client';

/**
 * Response shape for the internal user info endpoint.
 * All fields are optional to guard against partial API responses.
 *
 * Source: a/consumer/api/v0/institutions/{institutionId}/users/{userId}/info
 *
 * @interim This implementation is a bespoke stand-in until an external dependency
 * provides this functionality. When that package is ready, replace the
 * `InternalUsersClient` implementation and update the PWA DI factory in
 * `projects/pwa/src/index.ts` — no other files should need to change.
 */
export interface UserInfo {
    userId?: string;
    roleId?: string;
    roleName?: string;
    roleDescription?: string;
    portfolioId?: string;
    portfolioName?: string;
    groupId?: string;
    userDefinedGroup?: boolean;
    instId?: string;
}

/**
 * Contract for the internal users service.
 * Consumers depend on this interface so that the underlying implementation
 * can be swapped when the external dependency becomes available.
 */
export interface IInternalUsersService {
    getInternalUserDetails(institutionId: string, userId: string): Promise<UserInfo>;
}

/**
 * Interim bespoke HTTP client for internal user endpoints.
 *
 * @interim Replace with the official external dependency when available.
 * Only the DI factory in `projects/pwa/src/index.ts` needs updating at that point.
 */
export class InternalUsersClient implements IInternalUsersService {
    constructor(private readonly httpClient: TmBffHttpClient) {}

    public async getInternalUserDetails(institutionId: string, userId: string): Promise<UserInfo> {
        const url = `a/consumer/api/a/graph/api/v0/institutions/${institutionId}/users/${userId}/info`;
        return this.httpClient.request<UserInfo>(url, {
            method: 'GET',
        });
    }
}
